import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
});

function needsAdminAuth(url = "") {
  return (
    url.startsWith("/admin") ||
    url.startsWith("/suppliers") ||
    url.includes("/matches") ||
    url.includes("/match") ||
    /\/rfq\/[^/]+\/send$/.test(url) ||
    url.includes("/offer-desk") ||
    url.includes("/structured") ||
    /\/offers\/[^/]+$/.test(url) ||
    /\/offers\/[^/]+\/(publish|withdraw)$/.test(url) ||
    (url.startsWith("/purchase-orders/") && !url.endsWith("/approve")) ||
    url.startsWith("/inspections/") ||
    url.startsWith("/freightiq/") ||
    url.startsWith("/shipment-tracking/") ||
    (url.startsWith("/documents/") &&
      !url.endsWith("/download") &&
      !url.endsWith("/preview") &&
      !url.endsWith("/message"))
  );
}

function needsBuyerAuth(url = "") {
  return (
    url === "/workspace/me" ||
    url === "/rfq/intelligence" ||
    (/^\/rfq\/[^/]+\/confirm-field$/.test(url) && !!localStorage.getItem("demaxtore_buyer_token"))
  );
}

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("demaxtore_admin_token");
  const buyerToken = localStorage.getItem("demaxtore_buyer_token");
  const url = config.url || "";
  if (token && needsAdminAuth(url)) {
    config.headers.Authorization = `Bearer ${token}`;
  } else if (buyerToken && needsBuyerAuth(url)) {
    config.headers.Authorization = `Bearer ${buyerToken}`;
  }
  return config;
});

// RFQ
export const submitRfq = (payload) => api.post("/rfq/submit", payload).then((r) => r.data);
export const getRfq = (id) => api.get(`/rfq/${id}`).then((r) => r.data);
export const uploadSpec = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return axios
    .post(`${API}/rfq/upload-spec`, fd, { headers: { "Content-Type": "multipart/form-data" } })
    .then((r) => r.data);
};

// Intelligence
export const analyzeRfqIntelligence = (payload) =>
  api.post("/rfq/intelligence", payload).then((r) => r.data);
export const getStructuredRfq = (id) => api.get(`/rfq/${id}/structured`).then((r) => r.data);
export const confirmRfqField = (id, payload) =>
  api.post(`/rfq/${id}/confirm-field`, payload).then((r) => r.data);

export const exchangeWorkspaceAccess = (token) =>
  api.get("/workspace/access", { params: { token } }).then((r) => r.data);

export const requestWorkspaceAccess = (rfqId, email) =>
  api.post(`/rfq/${rfqId}/workspace-access`, { email }).then((r) => r.data);

export function saveBuyerSession(data) {
  if (data?.session_token) {
    localStorage.setItem("demaxtore_buyer_token", data.session_token);
  }
  if (data?.buyer_id) localStorage.setItem("demaxtore_buyer_id", data.buyer_id);
  if (data?.workspace_id) localStorage.setItem("demaxtore_workspace_id", data.workspace_id);
  if (data?.rfq_id) localStorage.setItem("demaxtore_rfq_id", data.rfq_id);
}

export function getBuyerSession() {
  return {
    buyer_id: localStorage.getItem("demaxtore_buyer_id"),
    workspace_id: localStorage.getItem("demaxtore_workspace_id"),
    token: localStorage.getItem("demaxtore_buyer_token"),
  };
}

export function clearBuyerSession() {
  localStorage.removeItem("demaxtore_buyer_token");
  localStorage.removeItem("demaxtore_buyer_id");
  localStorage.removeItem("demaxtore_workspace_id");
  localStorage.removeItem("demaxtore_rfq_id");
}

export const registerWorkspace = (payload) =>
  api.post("/workspace/register", payload).then((r) => r.data);

export const loginWorkspace = (payload) =>
  api.post("/workspace/login", payload).then((r) => r.data);

export const getWorkspaceMe = () => api.get("/workspace/me").then((r) => r.data);

export function getWorkspaceUrl(data) {
  if (data?.workspace_access_url) return data.workspace_access_url;
  const base = (process.env.REACT_APP_WORKSPACE_URL || window.location.origin).replace(/\/$/, "");
  if (data?.buyer_id && data?.workspace_id) {
    return `${base}/workspace?buyer_id=${data.buyer_id}&workspace_id=${data.workspace_id}`;
  }
  return `${base}/workspace`;
}

export function buildConfirmationState(res) {
  if (!res) return {};
  return {
    rfq_number: res.rfq_number,
    workspace_access_url: res.workspace_access_url,
    buyer_id: res.buyer_id,
    workspace_id: res.workspace_id,
    session_token: res.session_token,
  };
}

// Admin auth & RFQ list
export const adminLogin = (email, password) =>
  api.post("/admin/login", { email, password }).then((r) => r.data);
export const adminListRfqs = (params = {}) => api.get("/admin/rfqs", { params }).then((r) => r.data);
export const adminStats = () => api.get("/admin/stats").then((r) => r.data);
export const getAdminIntelligence = (id) =>
  api.get(`/admin/rfq/${id}/intelligence`).then((r) => r.data);
export const getAdminOperations = (id) =>
  api.get(`/admin/rfq/${id}/operations`).then((r) => r.data);
export const getAdminActivity = (id) =>
  api.get(`/admin/rfq/${id}/activity`).then((r) => r.data);

// Matching
export const listSuppliers = (params = {}) => api.get("/suppliers", { params }).then((r) => r.data);
export const uploadSupplierLogo = (supplierId, file) => {
  const fd = new FormData();
  fd.append("file", file);
  return axios
    .post(`${API}/suppliers/${supplierId}/logo`, fd, {
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: `Bearer ${localStorage.getItem("demaxtore_admin_token") || ""}`,
      },
    })
    .then((r) => r.data);
};
export const uploadSupplierCatalog = (supplierId, file) => {
  const fd = new FormData();
  fd.append("file", file);
  return axios
    .post(`${API}/suppliers/${supplierId}/catalog`, fd, {
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: `Bearer ${localStorage.getItem("demaxtore_admin_token") || ""}`,
      },
    })
    .then((r) => r.data);
};
export const getRfqMatches = (id, params = {}) =>
  api.get(`/rfq/${id}/matches`, { params }).then((r) => r.data);
export const addRfqMatch = (id, payload) =>
  api.post(`/rfq/${id}/match`, payload).then((r) => r.data);
export const removeRfqMatch = (id, supplierId, params = {}) =>
  api.delete(`/rfq/${id}/match/${supplierId}`, { params }).then((r) => r.data);
export const sendRfqToSuppliers = (id, payload) =>
  api.post(`/rfq/${id}/send`, payload).then((r) => r.data);

// Offers
export const getAdminOfferDesk = (id) =>
  api.get(`/admin/rfq/${id}/offer-desk`).then((r) => r.data);
export const createRfqOffer = (rfqId, payload) =>
  api.post(`/rfq/${rfqId}/offers`, payload).then((r) => r.data);
export const updateOffer = (offerId, payload) =>
  api.put(`/offers/${offerId}`, payload).then((r) => r.data);
export const publishOffer = (offerId) =>
  api.post(`/offers/${offerId}/publish`).then((r) => r.data);
export const getOfferComparison = (rfqId, params = {}) =>
  api.get(`/rfq/${rfqId}/comparison`, { params }).then((r) => r.data);
export const getOffer = (offerId) => api.get(`/offers/${offerId}`).then((r) => r.data);
export const selectOffer = (offerId) =>
  api.post(`/offers/${offerId}/select`).then((r) => r.data);
export const clarifyOffer = (offerId, message) =>
  api.post(`/offers/${offerId}/clarify`, { message }).then((r) => r.data);

// Purchase orders
export const listWorkspacePurchaseOrders = (params = {}) =>
  api.get("/workspace/purchase-orders", { params }).then((r) => r.data);
export const getPurchaseOrder = (id) => api.get(`/purchase-orders/${id}`).then((r) => r.data);
export const updatePurchaseOrder = (id, payload) =>
  api.put(`/purchase-orders/${id}`, payload).then((r) => r.data);
export const approvePurchaseOrder = (id) =>
  api.post(`/purchase-orders/${id}/approve`).then((r) => r.data);
export const confirmPurchaseOrder = (id) =>
  api.post(`/purchase-orders/${id}/confirm`).then((r) => r.data);
export const startPoProduction = (id) =>
  api.post(`/purchase-orders/${id}/start-production`).then((r) => r.data);
export const markPoReadyForInspection = (id) =>
  api.post(`/purchase-orders/${id}/ready-for-inspection`).then((r) => r.data);
export const sendPoMessage = (id, message) =>
  api.post(`/purchase-orders/${id}/message`, { message }).then((r) => r.data);

// Inspections
export const listWorkspaceInspections = (params = {}) =>
  api.get("/workspace/inspections", { params }).then((r) => r.data);
export const getInspection = (id) => api.get(`/inspections/${id}`).then((r) => r.data);
export const updateInspection = (id, payload) =>
  api.put(`/inspections/${id}`, payload).then((r) => r.data);
export const scheduleInspection = (id, payload) =>
  api.post(`/inspections/${id}/schedule`, payload).then((r) => r.data);
export const assignInspector = (id, payload) =>
  api.post(`/inspections/${id}/assign`, payload).then((r) => r.data);
export const startInspection = (id) =>
  api.post(`/inspections/${id}/start`).then((r) => r.data);
export const completeInspection = (id) =>
  api.post(`/inspections/${id}/complete`).then((r) => r.data);
export const saveInspectionResult = (id, payload) =>
  api.post(`/inspections/${id}/result`, payload).then((r) => r.data);
export const uploadInspectionAttachments = (id, files) => {
  const fd = new FormData();
  (files || []).forEach((f) => fd.append("files", f));
  return axios
    .post(`${API}/inspections/${id}/attachments`, fd, {
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: `Bearer ${localStorage.getItem("demaxtore_admin_token") || ""}`,
      },
    })
    .then((r) => r.data);
};
export const sendInspectionMessage = (id, message) =>
  api.post(`/inspections/${id}/message`, { message }).then((r) => r.data);

// FreightIQ
export const listWorkspaceFreightiq = (params = {}) =>
  api.get("/workspace/freightiq", { params }).then((r) => r.data);
export const getFreightiq = (id) => api.get(`/freightiq/${id}`).then((r) => r.data);
export const updateFreightiq = (id, payload) =>
  api.put(`/freightiq/${id}`, payload).then((r) => r.data);
export const selectFreightCarrier = (id, payload) =>
  api.post(`/freightiq/${id}/carrier`, payload).then((r) => r.data);
export const saveFreightBooking = (id, payload) =>
  api.post(`/freightiq/${id}/booking`, payload).then((r) => r.data);
export const assignFreightContainer = (id, payload) =>
  api.post(`/freightiq/${id}/container`, payload).then((r) => r.data);
export const updateFreightEta = (id, payload) =>
  api.post(`/freightiq/${id}/eta`, payload).then((r) => r.data);
export const updateFreightStatus = (id, payload) =>
  api.post(`/freightiq/${id}/status`, payload).then((r) => r.data);
export const sendFreightiqMessage = (id, message) =>
  api.post(`/freightiq/${id}/message`, { message }).then((r) => r.data);

// Shipments
export const listWorkspaceShipments = (params = {}) =>
  api.get("/workspace/shipments", { params }).then((r) => r.data);
export const getShipmentTracking = (id) =>
  api.get(`/shipment-tracking/${id}`).then((r) => r.data);
export const updateShipmentTracking = (id, payload) =>
  api.put(`/shipment-tracking/${id}`, payload).then((r) => r.data);
export const addShipmentEvent = (id, payload) =>
  api.post(`/shipment-tracking/${id}/event`, payload).then((r) => r.data);
export const updateShipmentEta = (id, payload) =>
  api.post(`/shipment-tracking/${id}/eta`, payload).then((r) => r.data);
export const updateShipmentStatus = (id, payload) =>
  api.post(`/shipment-tracking/${id}/status`, payload).then((r) => r.data);
export const updateShipmentMilestone = (id, payload) =>
  api.post(`/shipment-tracking/${id}/milestone`, payload).then((r) => r.data);
export const sendShipmentMessage = (id, message) =>
  api.post(`/shipment-tracking/${id}/message`, { message }).then((r) => r.data);

// Documents
export const listDocumentTypes = () => api.get("/documents/types/list").then((r) => r.data);
export const searchWorkspaceDocuments = (params = {}) =>
  api.get("/workspace/documents/search", { params }).then((r) => r.data);
export const getDocument = (id) => api.get(`/documents/${id}`).then((r) => r.data);
export const createDocument = (payload) => api.post("/documents", payload).then((r) => r.data);
export const updateDocument = (id, payload) =>
  api.put(`/documents/${id}`, payload).then((r) => r.data);
export const uploadDocumentVersion = (id, file, meta = {}) => {
  const fd = new FormData();
  fd.append("file", file);
  Object.entries(meta).forEach(([k, v]) => {
    if (v != null) fd.append(k, v);
  });
  return axios
    .post(`${API}/documents/${id}/version`, fd, {
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: `Bearer ${localStorage.getItem("demaxtore_admin_token") || ""}`,
      },
    })
    .then((r) => r.data);
};
export const archiveDocument = (id) =>
  api.post(`/documents/${id}/archive`).then((r) => r.data);
export const restoreDocument = (id) =>
  api.post(`/documents/${id}/restore`).then((r) => r.data);
export const sendDocumentMessage = (id, message) =>
  api.post(`/documents/${id}/message`, { message }).then((r) => r.data);

// Workspace dashboard
export const getWorkspaceDashboard = (params = {}) =>
  api.get("/workspace/dashboard", { params }).then((r) => r.data);
export const getWorkspaceActivities = (params = {}) =>
  api.get("/workspace/activities", { params }).then((r) => r.data);
export const getWorkspaceAlerts = (params = {}) =>
  api.get("/workspace/alerts", { params }).then((r) => r.data);
export const getWorkspaceTasks = (params = {}) =>
  api.get("/workspace/tasks", { params }).then((r) => r.data);
export const searchWorkspace = (q, params = {}) =>
  api.get("/workspace/search", { params: { q, ...params } }).then((r) => r.data);
export const getWorkspaceNotifications = (params = {}) =>
  api.get("/workspace/notifications", { params }).then((r) => r.data);
export const markWorkspaceNotificationsRead = (payload) =>
  api.post("/workspace/notifications/read", payload).then((r) => r.data);
export const archiveWorkspaceNotifications = (payload) =>
  api.post("/workspace/notifications/archive", payload).then((r) => r.data);
export const saveWorkspacePreferences = (payload) =>
  api.post("/workspace/preferences", payload).then((r) => r.data);
export const getWorkspacePreferences = (params = {}) =>
  api.get("/workspace/preferences", { params }).then((r) => r.data);
